/**
 * Import Monitor Debug tab.
 *
 * Shows the raw middleware payloads for every failed and validation-skipped
 * record on the latest job per data type. Unlike debug_sample_raw (first 10
 * records only), this pulls ImportLog rows where the raw_record has been
 * persisted on fields_changed during import, so failures at any row are
 * inspectable.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  importsAPI,
  type ImportJob,
  type ImportLog,
  type ImportSource,
} from '../../lib/api/imports';

interface ImportDebugTabProps {
  jobs: ImportJob[];
  source: ImportSource;
}

type StageFilter = 'failed' | 'skipped' | 'success';

export default function ImportDebugTab({ jobs, source }: ImportDebugTabProps) {
  const latestPerType = useMemo(() => {
    const map = new Map<string, ImportJob>();
    for (const j of jobs) {
      const existing = map.get(j.data_type);
      if (!existing || new Date(j.created_at) > new Date(existing.created_at)) {
        map.set(j.data_type, j);
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.data_type_display.localeCompare(b.data_type_display),
    );
  }, [jobs]);

  const [selectedJobId, setSelectedJobId] = useState<string | null>(
    latestPerType[0]?.job_id ?? null,
  );
  const [stage, setStage] = useState<StageFilter>('failed');
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedJobId && latestPerType[0]) {
      setSelectedJobId(latestPerType[0].job_id);
    }
  }, [latestPerType, selectedJobId]);

  useEffect(() => {
    if (!selectedJobId) {
      setLogs([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setExpandedLogId(null);
    const fetchLogs = async () => {
      if (stage === 'success') {
        const [createdRes, updatedRes] = await Promise.all([
          importsAPI.getJobLogs(selectedJobId, { action: 'created', page: 1 }, source),
          importsAPI.getJobLogs(selectedJobId, { action: 'updated', page: 1 }, source),
        ]);
        return [...(createdRes.results ?? []), ...(updatedRes.results ?? [])];
      }
      const res = await importsAPI.getJobLogs(selectedJobId, { action: stage, page: 1 }, source);
      return res.results ?? [];
    };
    fetchLogs()
      .then(results => {
        if (cancelled) return;
        setLogs(
          results
            .filter(l => (l.fields_changed as { raw_record?: unknown } | undefined)?.raw_record)
            .sort((a, b) => (a.source_row ?? 0) - (b.source_row ?? 0)),
        );
      })
      .catch(err => {
        if (!cancelled) toast.error(`Failed to load logs: ${err?.message ?? 'unknown'}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedJobId, stage, source]);

  if (latestPerType.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-10">
        No recent jobs yet for this source.
      </div>
    );
  }

  const selectedJob = latestPerType.find(j => j.job_id === selectedJobId);
  const badgeCount = (j: ImportJob) =>
    stage === 'failed' ? j.records_failed
      : stage === 'skipped' ? j.records_skipped
      : j.records_created + j.records_updated;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium mb-2">Latest job per data type</p>
        <div className="flex flex-wrap gap-1">
          {latestPerType.map(j => {
            const count = badgeCount(j);
            return (
              <button
                key={j.job_id}
                onClick={() => setSelectedJobId(j.job_id)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  selectedJobId === j.job_id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:bg-muted'
                }`}
                title={`${j.status} • ${j.records_failed} failed / ${j.records_skipped} skipped / ${j.total_records} total`}
              >
                {j.data_type_display}
                <span className={`ml-1.5 text-[10px] ${count > 0 ? 'opacity-100' : 'opacity-60'}`}>
                  {count > 0 ? `${count.toLocaleString()} ${stage}` : j.status}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-1 items-center">
        {(['failed', 'skipped', 'success'] as const).map(s => (
          <button
            key={s}
            onClick={() => setStage(s)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              stage === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
          >
            {s === 'failed' ? 'Failed' : s === 'skipped' ? 'Skipped' : 'Success'}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-2">
          (sampled — first {stage === 'failed' ? 10 : 5} per job)
        </span>
      </div>

      {selectedJob && (
        <div className="text-xs text-muted-foreground">
          Job ID: <span className="font-mono">{selectedJob.job_id}</span> ·{' '}
          Created: {new Date(selectedJob.created_at).toLocaleString()} ·{' '}
          Total: {selectedJob.total_records.toLocaleString()} (failed{' '}
          {selectedJob.records_failed.toLocaleString()}, skipped{' '}
          {selectedJob.records_skipped.toLocaleString()})
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : logs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          {stage === 'failed'
            ? 'No failed records on this job.'
            : `No ${stage} records with captured payload on this job yet.`}
        </p>
      ) : (
        <div className="space-y-2">
          {logs.map(log => {
            const rawRecord = (
              log.fields_changed as { raw_record?: Record<string, unknown> } | undefined
            )?.raw_record;
            const expanded = expandedLogId === log.id;
            return (
              <div key={log.id} className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => setExpandedLogId(expanded ? null : log.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs">
                      row {log.source_row ?? '-'} · {log.action_display || log.action} · {log.entity_reference}
                    </div>
                    {log.error_message && (
                      <div className={`text-xs truncate ${
                        log.action === 'failed' ? 'text-red-600' : 'text-muted-foreground'
                      }`}>
                        {log.error_code ? `[${log.error_code}] ` : ''}{log.error_message}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground ml-2 shrink-0">
                    {rawRecord ? (expanded ? 'hide payload' : 'view payload') : 'no payload'}
                  </span>
                </button>
                {expanded && rawRecord && (
                  <div className="border-t p-3 bg-slate-50">
                    <pre className="text-xs font-mono whitespace-pre-wrap break-all max-h-96 overflow-auto">
                      {JSON.stringify(rawRecord, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
